/**
 * `/admin/incontri` — il calendario degli incontri di progetto.
 *
 * `project.incontro` esisteva dal primo giorno e alimenta il KPI «Prossimo
 * incontro» della Panoramica, ma non aveva **un solo endpoint mutante**:
 * l'unico modo di scriverci era una INSERT a mano in psql. Con quel costo,
 * nessuno l'ha più fatto — l'ultima riga in produzione era il 21 maggio, e per
 * tre mesi il portale ha detto a Enzo «Nessun incontro pianificato» mentre le
 * call si tenevano regolarmente.
 *
 * Diverso da `/admin/conversazioni`, che registra il **passato** (chiamate ed
 * email già avvenute): qui c'è quello che deve ancora succedere.
 */

import { ArrowLeft, CalendarPlus, Eye, EyeOff, Loader2, Trash2 } from "lucide-react";
import { useState } from "react";
import { Link, Navigate } from "react-router-dom";

import { PageContainer } from "@/components/PageContainer";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import {
  useAdminIncontri,
  useAggiornaIncontro,
  useCreaIncontro,
  useEliminaIncontro,
} from "@/hooks/useAdminIncontri";
import { useMe } from "@/hooks/useMe";
import { isMaitimeStaff } from "@/lib/nav-config";
import { cn } from "@/lib/utils";
import type { Incontro } from "@/types/api";

const INPUT_CLS =
  "w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring";

function formatDataOra(iso: string): string {
  return new Date(iso).toLocaleString("it-IT", {
    weekday: "short",
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function isFuturo(iso: string): boolean {
  return new Date(iso).getTime() >= Date.now();
}

export function AdminIncontriPage() {
  const { data: me, isLoading: loadingMe } = useMe();
  const { data: incontri = [], isLoading } = useAdminIncontri();

  if (!loadingMe && me && !isMaitimeStaff(me)) {
    return <Navigate to="/" replace />;
  }

  const futuri = incontri.filter((i) => isFuturo(i.data_ora));
  const passati = incontri.filter((i) => !isFuturo(i.data_ora));

  return (
    <PageContainer>
      <Link
        to="/admin"
        className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors w-fit"
      >
        <ArrowLeft className="size-3.5" />
        Backoffice
      </Link>

      <header className="space-y-1">
        <p className="text-xs uppercase tracking-wider text-muted-foreground">
          Backoffice Maitime
        </p>
        <h1 className="text-3xl font-semibold tracking-tight text-foreground">
          Incontri
        </h1>
        <p className="text-sm text-muted-foreground max-w-2xl pt-1">
          Le call e gli incontri in programma. Il primo appuntamento futuro visibile al
          cliente è quello che compare nel KPI «Prossimo incontro» della Panoramica.
          Per registrare cosa è già successo usa invece{" "}
          <Link to="/admin/conversazioni" className="underline hover:text-foreground">
            Conversazioni
          </Link>
          .
        </p>
      </header>

      <NuovoIncontro />

      {isLoading ? (
        <Skeleton className="h-64 w-full" />
      ) : (
        <div className="space-y-6">
          <Sezione
            titolo="In programma"
            vuoto="Nessun incontro futuro — il KPI del cliente dice «Nessun incontro pianificato»."
            incontri={futuri}
            evidenziaPrimo
          />
          <Sezione titolo="Passati" vuoto="Nessuno storico." incontri={passati} />
        </div>
      )}
    </PageContainer>
  );
}

function Sezione({
  titolo,
  vuoto,
  incontri,
  evidenziaPrimo = false,
}: {
  titolo: string;
  vuoto: string;
  incontri: Incontro[];
  evidenziaPrimo?: boolean;
}) {
  // La lista arriva desc dal backend; i futuri si leggono meglio dal più vicino.
  const ordinati = evidenziaPrimo ? [...incontri].reverse() : incontri;

  return (
    <section className="space-y-2">
      <h2 className="text-xs uppercase tracking-wider text-muted-foreground">
        {titolo}
      </h2>
      {ordinati.length === 0 ? (
        <p className="rounded-lg border bg-card/40 px-4 py-3 text-sm text-muted-foreground">
          {vuoto}
        </p>
      ) : (
        <ul className="rounded-lg border bg-card divide-y">
          {ordinati.map((i, idx) => (
            <IncontroRow
              key={i.id}
              incontro={i}
              // Il primo futuro è quello che il cliente vede davvero.
              inVetrina={evidenziaPrimo && idx === 0 && i.is_visibile_cliente}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

function IncontroRow({
  incontro: i,
  inVetrina,
}: {
  incontro: Incontro;
  inVetrina: boolean;
}) {
  const aggiorna = useAggiornaIncontro();
  const elimina = useEliminaIncontro();
  const [confermaElimina, setConfermaElimina] = useState(false);
  const isPending = aggiorna.isPending || elimina.isPending;

  return (
    <li className={cn("flex items-start gap-4 px-4 py-3", isPending && "opacity-50")}>
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium">{formatDataOra(i.data_ora)}</span>
          {inVetrina && (
            <Badge variant="outline" className="text-[10px] border-success/40 text-success">
              nel KPI del cliente
            </Badge>
          )}
        </div>
        <p className="text-sm text-foreground">{i.titolo}</p>
        {(i.luogo || i.partecipanti_label) && (
          <p className="text-xs text-muted-foreground">
            {[i.luogo, i.partecipanti_label].filter(Boolean).join(" · ")}
          </p>
        )}
        {i.descrizione && (
          <p className="text-xs text-muted-foreground leading-relaxed">{i.descrizione}</p>
        )}
      </div>

      <div className="flex items-center gap-1.5 shrink-0">
        <button
          type="button"
          disabled={isPending}
          onClick={() =>
            aggiorna.mutate({
              id: i.id,
              body: { is_visibile_cliente: !i.is_visibile_cliente },
            })
          }
          className={cn(
            "inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs transition-colors",
            "disabled:cursor-not-allowed disabled:opacity-50",
            i.is_visibile_cliente
              ? "bg-success/10 text-success border-success/30 hover:bg-success/20"
              : "bg-muted text-muted-foreground border-muted-foreground/20 hover:bg-muted/70"
          )}
          title={
            i.is_visibile_cliente
              ? "Visibile al cliente — click per nascondere"
              : "Interno — click per mostrarlo al cliente"
          }
        >
          {i.is_visibile_cliente ? (
            <>
              <Eye className="size-3.5" /> Visibile
            </>
          ) : (
            <>
              <EyeOff className="size-3.5" /> Interno
            </>
          )}
        </button>

        {confermaElimina ? (
          <div className="flex items-center gap-1">
            <Button
              size="sm"
              variant="destructive"
              disabled={isPending}
              onClick={() => elimina.mutate(i.id)}
            >
              Conferma
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setConfermaElimina(false)}>
              No
            </Button>
          </div>
        ) : (
          <Button
            size="icon"
            variant="ghost"
            disabled={isPending}
            onClick={() => setConfermaElimina(true)}
            aria-label="Elimina incontro"
          >
            <Trash2 className="size-4" />
          </Button>
        )}
      </div>
    </li>
  );
}

function NuovoIncontro() {
  const crea = useCreaIncontro();
  const [aperto, setAperto] = useState(false);
  const [dataOra, setDataOra] = useState("");
  const [titolo, setTitolo] = useState("");
  const [luogo, setLuogo] = useState("");
  const [partecipanti, setPartecipanti] = useState("");
  const [descrizione, setDescrizione] = useState("");
  const [visibile, setVisibile] = useState(true);

  const valido = dataOra !== "" && titolo.trim() !== "";

  function salva() {
    if (!valido) return;
    crea.mutate(
      {
        // `datetime-local` è senza fuso: lo interpretiamo nel fuso del browser
        // e lo mandiamo in ISO, che è quello che il backend si aspetta.
        data_ora: new Date(dataOra).toISOString(),
        titolo: titolo.trim(),
        luogo: luogo.trim() || null,
        partecipanti_label: partecipanti.trim() || null,
        descrizione: descrizione.trim() || null,
        is_visibile_cliente: visibile,
      },
      {
        onSuccess: () => {
          setDataOra("");
          setTitolo("");
          setLuogo("");
          setPartecipanti("");
          setDescrizione("");
          setVisibile(true);
          setAperto(false);
        },
      }
    );
  }

  if (!aperto) {
    return (
      <Button variant="outline" className="w-fit" onClick={() => setAperto(true)}>
        <CalendarPlus className="size-4" />
        Fissa un incontro
      </Button>
    );
  }

  return (
    <div className="rounded-lg border bg-card p-5 space-y-4">
      <h2 className="text-sm font-medium">Nuovo incontro</h2>

      <div className="grid gap-3 md:grid-cols-2">
        <label className="space-y-1">
          <span className="text-xs text-muted-foreground">Data e ora *</span>
          <input
            type="datetime-local"
            value={dataOra}
            onChange={(e) => setDataOra(e.target.value)}
            className={INPUT_CLS}
          />
        </label>
        <label className="space-y-1">
          <span className="text-xs text-muted-foreground">Titolo *</span>
          <input
            type="text"
            value={titolo}
            onChange={(e) => setTitolo(e.target.value)}
            placeholder="Allineamento settimanale"
            className={INPUT_CLS}
          />
        </label>
        <label className="space-y-1">
          <span className="text-xs text-muted-foreground">Luogo</span>
          <input
            type="text"
            value={luogo}
            onChange={(e) => setLuogo(e.target.value)}
            placeholder="Videocall · Sede Mister Pet"
            className={INPUT_CLS}
          />
        </label>
        <label className="space-y-1">
          <span className="text-xs text-muted-foreground">Partecipanti</span>
          <input
            type="text"
            value={partecipanti}
            onChange={(e) => setPartecipanti(e.target.value)}
            placeholder="Enzo · Enrico · Christian"
            className={INPUT_CLS}
          />
        </label>
      </div>

      <label className="space-y-1 block">
        <span className="text-xs text-muted-foreground">Note</span>
        <Textarea
          value={descrizione}
          onChange={(e) => setDescrizione(e.target.value)}
          rows={2}
          placeholder="Ordine del giorno, cosa portare…"
        />
      </label>

      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={visibile}
          onChange={(e) => setVisibile(e.target.checked)}
          className="size-4"
        />
        Visibile al cliente
      </label>

      <div className="flex items-center gap-2">
        <Button onClick={salva} disabled={!valido || crea.isPending}>
          {crea.isPending && <Loader2 className="size-4 animate-spin" />}
          Salva
        </Button>
        <Button variant="ghost" onClick={() => setAperto(false)}>
          Annulla
        </Button>
      </div>
    </div>
  );
}
